import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import TreeSelect from 'antd/lib/tree-select'
import cx from 'classnames'
import Form from 'antd/lib/form'
import { listSize } from 'config'
import cloneDeep from 'lodash/cloneDeep'
import { rootActions } from '@store/slices'
import { RootState } from '@store/store'
import useStopPropagarionClickPopup from '@hooks/processor/useStopPropagarionClickPopup'
import { isValidMongoId } from '@utils/helpers/commonHelpers'
import arrowSelect from '@assets/svg/caret_down.svg'
import styles from './index.scss'

interface IProps {
    module?: string
    parentKey?: string
    listName?: string
    limit?: number
    label?: string
    name: string
    projections?: string[]
    valueOption?: string
    keyOption?: string
    displayOption: string[] | string
    sort?: string
    formMode?: 'create' | 'edit'
    query?: any
    searchKeys?: string[]
    disabledAPI?: boolean
    renderDisplayOption?: any
    allowClear?: boolean
    onSelect?: any
    onChange?: any
    required?: boolean
    requiredMsg?: string
    rules?: any
    options?: any
    disabled?: boolean
    loading?: boolean
    theme?: string
    multiple?: boolean
    help?: string
    editValue?: string
    editRecord?: any
    modalForm?: boolean
    hideSelectedLabels?: boolean
    containerId?: string
    maxTagCount?: number | 'responsive'
    fieldKey?: string
    treeCheckable?: boolean
    lazy?: boolean
}

const TreeSelectField = (props: IProps) => {
    const {
        module = '',
        parentKey = '',
        limit = 0,
        listName = 'list',
        label = '',
        name,
        projections = [],
        valueOption = '_id',
        keyOption = '_id',
        displayOption,
        sort = '-createdAt',
        editValue = null,
        query = {},
        searchKeys = [],
        allowClear = true,
        onSelect = undefined,
        onChange = undefined,
        required = false,
        requiredMsg = '',
        rules = [],
        disabled = false,
        multiple = false,
        help = '',
        editRecord = null,
        modalForm = false,
        hideSelectedLabels = false,
        maxTagCount = 'responsive',
        containerId = '',
        fieldKey = '',
        options = [],
        treeCheckable = true,
        lazy = true
    } = props
    const dispatch = useDispatch()
    const { data: i18n } = useSelector((state: RootState) => state.localization.locals)
    const id = 'tree-select-' + fieldKey + name
    useStopPropagarionClickPopup(id)

    const [state, setState] = useState<any>({
        list: [],
        searchedData: [],
        editRecord: {},
        isLoading: false,
        searchValue: '',
        skip: 0,
        total: 0
    })
    const [focus, setFocus] = useState<boolean>(false)
    const [open, setOpen] = useState<boolean>(false)

    const onSearch = searchValue => {
        const satisfiedSearch = values => {
            return values.map(item => ({
                key: item.key,
                value: item.value,
                title: item.title,
                children: satisfiedSearch(
                    item.children.filter(x => x.title?.toLowerCase()?.includes(searchValue?.toLowerCase()))
                )
            }))
        }
        const searchedData = satisfiedSearch(state.list).filter(
            item => item.title?.toLowerCase()?.includes(searchValue?.toLowerCase()?.trim()) || item.children.length
        )
        setState(state => ({
            ...state,
            searchedData,
            searchValue,
            skip: 0,
            isLoading: true
        }))
    }

    const handleGetData = useCallback(
        (mode, skip = 0, editValue = null) => {
            if (options.length) {
                fillForm(options, options.length, mode)
            } else if (module) {
                const params: any = {
                    pagination: {
                        limit: limit || listSize,
                        hasTotal: true,
                        skip
                    },
                    query: { ...query },
                    sagaCB: {
                        onSuccess: result => {
                            fillForm(result?.data || result || [], result?.total || 0, mode)
                        }
                    }
                }
                if (editValue) {
                    params.query = {
                        _id: editValue
                    }
                }
                if (projections?.length) {
                    params.necessaryProjectionArray = projections
                }
                if (sort?.length) {
                    params.pagination.sort = sort
                }
                dispatch(rootActions[module][listName].onRequest(params))
            }
        },
        [JSON.stringify(query), module, options.length]
    )
    const fillForm = (resultsData, total, mode) => {
        if (editValue) {
            setState(state => ({
                ...state,
                editRecord: resultsData.length ? resultsData[0] : {}
            }))
        } else {
            let list = state.skip > 0 ? [...state.list, ...resultsData] : resultsData
            if (parentKey) {
                const getChildren = (values, isRoot = false) =>
                    values.map((item, index) => {
                        const children = getChildren(list.filter(x => x[parentKey] && x[parentKey] === item[keyOption]))
                        return {
                            value: item[valueOption] + (typeof onChange === 'function' ? `___key` : ''),
                            key: item[keyOption] + index.toString(),
                            title: typeof displayOption === 'string' ? item[displayOption] : '',
                            children
                        }
                    })
                list = getChildren(
                    list.filter(item => item[valueOption] && !item[parentKey]),
                    true
                )
            }
            setState(state => ({
                ...state,
                list,
                isLoading: false,
                skip: mode === 'initial' ? listSize : state.skip + listSize,
                total
            }))
        }
    }
    const refetch = useRef(true)
    const refetchDeps = [JSON.stringify(query), handleGetData]

    useEffect(() => {
        refetch.current = true
    }, refetchDeps)

    useEffect(() => {
        if (!open) {
            setState(state => ({
                ...state,
                searchValue: ''
            }))
        }
        if (!options.length && ((lazy && !open) || !refetch.current)) return
        refetch.current = false
        setState(state => ({
            ...state,
            list: [],
            isLoading: true,
            skip: 0,
            total: 0
        }))
        handleGetData('initial', 0)
    }, [...refetchDeps, open, lazy])

    const allOptions = cloneDeep(state.list)
    if (editValue && state.editRecord?._id && !allOptions.find(item => item._id === state.editRecord?._id)) {
        allOptions.push(state.editRecord)
    }
    if (editRecord?._id) {
        allOptions.push(editRecord)
    } else if (Array.isArray(editRecord)) {
        editRecord.forEach(item => {
            allOptions.push(item)
        })
    }
    return (
        <div id={id}>
            <Form.Item
                noStyle
                shouldUpdate={(prev, next) =>
                    multiple ? JSON.stringify(prev[name]) !== JSON.stringify(next[name]) : prev[name] !== next[name]
                }
            >
                {({ getFieldValue }) => {
                    const modifier = getFieldValue(name)
                    const value = multiple && typeof modifier !== 'string' ? modifier?.[0] : modifier
                    return (
                        <div className={styles['tree-select-field']}>
                            <label
                                className={cx(styles.label, {
                                    [styles.asPlacehoder]: value?.length || focus
                                })}
                            >
                                {label ? label + (required ? ' *' : '') : ''}
                            </label>
                            <Form.Item
                                style={{ marginBottom: help?.trim() ? 4 : 'auto' }}
                                name={name}
                                rules={[
                                    {
                                        required: required,
                                        message:
                                            requiredMsg || `${i18n?.General?.Please_choose || 'Please choose'} ${label}`
                                    },
                                    ...rules
                                ]}
                            >
                                <TreeSelect
                                    treeNodeFilterProp="title"
                                    onSearch={searchKeys.length > 0 ? onSearch : undefined}
                                    style={{ width: '100%' }}
                                    showSearch={searchKeys.length > 0}
                                    loading={state.isLoading}
                                    allowClear={allowClear}
                                    onSelect={onSelect}
                                    onFocus={() => setFocus(true)}
                                    onBlur={() => setFocus(false)}
                                    maxTagCount={maxTagCount}
                                    treeCheckable={treeCheckable}
                                    treeDefaultExpandAll
                                    getPopupContainer={() => {
                                        if (modalForm) {
                                            return document.body
                                        } else if (containerId) {
                                            return document.getElementById(containerId)
                                        }
                                        return document.getElementById(id)
                                    }}
                                    suffixIcon={<img src={arrowSelect} />}
                                    onChange={onChange}
                                    onDropdownVisibleChange={setOpen}
                                    disabled={disabled}
                                    className={
                                        hideSelectedLabels ||
                                        (!multiple &&
                                            isValidMongoId(value) &&
                                            !allOptions.map(item => item?.[valueOption]).includes(value))
                                            ? styles['value-not-exist-in-options']
                                            : ''
                                    }
                                    popupClassName={styles['tree-select-options']}
                                    multiple={multiple}
                                    treeData={state.searchValue ? state.searchedData : state.list}
                                />
                            </Form.Item>
                        </div>
                    )
                }}
            </Form.Item>
            {help.trim() && <div className={styles['help-text']}>{help.trim()}</div>}
        </div>
    )
}

export default TreeSelectField
